/**
 * collection/get、market/get 详情页 symbol / id 解析（纯函数，无 Vue 依赖）
 */
import {
  filterListedMarkets,
  getDetailPrimaryMarket,
  getFirstListedMarket,
} from '@/utils/market-list-status.js';

/** 将 collection/get 或 market/get 单条响应归一化为市场详情页使用的 collection 结构。 */
export function normalizeMarketDetailPayload(raw) {
  if (!raw) return null;
  // collection/get：markets 字段以 API 为准，空 [] 不包装成假 market
  if (Array.isArray(raw.markets)) return raw;

  const m = raw;
  return {
    id: m.collectionId ?? m.id,
    title: m.title ?? m.question,
    image: m.image ?? m.icon ?? '',
    category: m.category,
    questionType: m.questionType,
    closed: m.closed,
    traders: m.traders,
    liquidity: m.liquidity,
    volume: m.volume,
    endDate: m.endDate,
    description: m.description,
    tags: m.tags,
    dataSource: m.dataSource,
    slug: m.slug,
    eventSlug: m.eventSlug,
    gammaSlug: m.gammaSlug,
    creatorUsername: m.creatorUsername ?? m.creator ?? m.creatorName,
    markets: [m],
  };
}

/** 搜索跳转详情：route.query.resolve 取值，表示 params.id 为 market id，需先 market/get */
export const MARKET_DETAIL_RESOLVE_FROM_MARKET = 'market';

export function isMarketDetailResolveFromMarket(query) {
  const raw = query?.resolve;
  const value = Array.isArray(raw) ? raw[0] : raw;
  return String(value ?? '').trim() === MARKET_DETAIL_RESOLVE_FROM_MARKET;
}

/** market/get 响应中的 collection 路由 id */
export function resolveCollIdFromMarketGet(data) {
  return String(data?.collId ?? data?.collectionId ?? data?.coll_id ?? '').trim();
}

/** 跳转市场详情路由时优先使用 collectionId / collId */
export function resolveMarketDetailRouteId(market) {
  return String(market?.collectionId ?? market?.collId ?? market?.id ?? '').trim();
}

/** 禁止写入 /market/:id 的噪音参数（标题 / 乐观 UI 由 API 提供） */
export const MARKET_DETAIL_URL_NOISE_KEYS = ['t', 'v', 'y', 'n', 'ps'];

export function sanitizeMarketDetailQuery(query = {}) {
  if (!query || typeof query !== 'object') return undefined;
  const next = {};
  for (const [key, value] of Object.entries(query)) {
    if (MARKET_DETAIL_URL_NOISE_KEYS.includes(key)) continue;
    if (value == null || value === '') continue;
    next[key] = value;
  }
  return Object.keys(next).length ? next : undefined;
}

export function marketDetailQueryNeedsStrip(query) {
  if (!query || typeof query !== 'object') return false;
  return MARKET_DETAIL_URL_NOISE_KEYS.some((key) =>
    Object.prototype.hasOwnProperty.call(query, key)
  );
}

/** beforeEnter：剥掉旧链接上的 t/v/y/n/ps */
export function ensureMarketDetailCleanQuery(to) {
  if (!marketDetailQueryNeedsStrip(to?.query)) return null;
  return {
    name: to.name,
    params: { ...to.params },
    query: sanitizeMarketDetailQuery(to.query),
    hash: to.hash,
    replace: true,
  };
}

/**
 * params.id 为 market id 时的详情路由（详情页会先 market/get 取 collId）。
 * @param {string|number} marketId
 * @param {Record<string, string>} [query]
 */
export function buildMarketDetailRouteFromMarketId(marketId, query = {}) {
  const id = String(marketId ?? '').trim();
  if (!id) return null;
  const sanitized = sanitizeMarketDetailQuery({
    resolve: MARKET_DETAIL_RESOLVE_FROM_MARKET,
    ...query,
  });
  return {
    name: 'MarketDetail',
    params: { id },
    ...(sanitized ? { query: sanitized } : {}),
  };
}

/**
 * params.id 为 collection id 时的详情路由（直接 collection/get）。
 * @param {string|number} collectionId
 * @param {Record<string, string>} [query]
 */
export function buildMarketDetailRouteFromCollectionId(collectionId, query = {}) {
  const id = String(collectionId ?? '').trim();
  if (!id) return null;
  const route = { name: 'MarketDetail', params: { id } };
  const sanitized = sanitizeMarketDetailQuery(query);
  if (sanitized) route.query = sanitized;
  return route;
}

/**
 * 按字段优先 collId，否则将 marketId 视为需 resolve 的 market id。
 * @param {{ collectionId?: string, collId?: string, marketId?: string, query?: Record<string, string> }} ids
 */
export function buildMarketDetailRouteFromIds({ collectionId, collId, marketId, query } = {}) {
  const coll = String(collectionId ?? collId ?? '').trim();
  if (coll) return buildMarketDetailRouteFromCollectionId(coll, query);
  return buildMarketDetailRouteFromMarketId(marketId, query);
}

/**
 * 字符串 `/market/:id` 若未带 resolve=market，补全为 market id 详情路由（保留其余 query）。
 * `/crypto/updown/:id` 保留为对象路由，尽量保留 / 补齐 sk。
 * @param {string|object|null|undefined} target
 */
export function enrichMarketDetailNavigation(target) {
  if (target == null || typeof target !== 'string') return target;
  const trimmed = target.trim();
  if (!trimmed || /^https?:\/\//i.test(trimmed)) return trimmed;

  try {
    const url = new URL(trimmed.startsWith('/') ? trimmed : `/${trimmed}`, 'http://local');

    const updownMatch = url.pathname.match(/^\/crypto\/updown\/([^/]+)$/);
    if (updownMatch) {
      const id = decodeURIComponent(updownMatch[1]);
      const query = {};
      for (const [key, value] of url.searchParams.entries()) {
        query[key] = value;
      }
      return {
        name: 'CryptoUpdownDetail',
        params: { id },
        query: Object.keys(query).length ? query : undefined,
      };
    }

    const sportLiveMatch = url.pathname.match(/^\/sport\/live\/([^/]+)$/);
    if (sportLiveMatch) {
      const matchId = decodeURIComponent(sportLiveMatch[1]);
      const query = {};
      for (const [key, value] of url.searchParams.entries()) {
        query[key] = value;
      }
      return {
        name: 'SportLiveMatchDetail',
        params: { matchId },
        query: Object.keys(query).length ? query : undefined,
      };
    }

    const pathMatch = url.pathname.match(/^\/market\/([^/]+)$/);
    if (!pathMatch) return trimmed;
    if (url.searchParams.get('resolve') === MARKET_DETAIL_RESOLVE_FROM_MARKET) return trimmed;

    const id = decodeURIComponent(pathMatch[1]);
    const extra = {};
    for (const [key, value] of url.searchParams.entries()) {
      if (key === 'resolve') continue;
      extra[key] = value;
    }
    return (
      buildMarketDetailRouteFromMarketId(id, extra) ||
      trimmed
    );
  } catch {
    return trimmed;
  }
}

/** myOrders / depth / pageMyPosition 等 symbol：collection/get 返回的 markets[].id */
export function resolveMarketTradeSymbol(market) {
  return String(market?.id ?? '').trim();
}

/** 评论 API 的 marketId / MARKET.targetId */
export function resolveCommentMarketId(data, fallbackRouteId = '') {
  const market = getFirstListedMarket(data?.markets);
  if (market) {
    const id = resolveMarketTradeSymbol(market);
    if (id) return id;
  }
  return String(fallbackRouteId ?? '').trim();
}

/** collection/get 返回的 markets[0].creator（用户 id） */
export function resolveMarketCreatorUserId(data) {
  const market = getFirstListedMarket(data?.markets);
  return String(market?.creator ?? '').trim();
}

/** 市场详情页 scope：当前 collection 下所有 markets[].id */
export function resolveMarketDetailMarketIds(data) {
  // 优先已上架；短周期结算后 listStatus=UNLISTED，持仓/订单仍需该档 symbol
  let list = filterListedMarkets(data?.markets);
  if (!list.length) {
    const primary = getDetailPrimaryMarket(data?.markets);
    list = primary ? [primary] : [];
  }
  const ids = list.map((m) => resolveMarketTradeSymbol(m)).filter(Boolean);
  return [...new Set(ids)];
}

/** 详情页参与人数展示基数（接口 traders 累加在其上，不覆盖） */
export const MARKET_DETAIL_PARTICIPANTS_BASE = 1000;

/** 参与人数：collection 下各 market 的 traders 之和 */
export function sumMarketTraders(collection) {
  if (!collection) return null;
  const markets = filterListedMarkets(collection.markets);
  if (markets.length) {
    let sum = 0;
    let hasValue = false;
    for (const m of markets) {
      const n = Number(m?.traders);
      if (Number.isFinite(n)) {
        sum += n;
        hasValue = true;
      }
    }
    if (hasValue) return sum;
  }
  const fallback = Number(collection.traders);
  return Number.isFinite(fallback) ? fallback : null;
}

/** 展示用参与人数：基数 1K + 接口 traders（null/无效按 0 处理） */
export function resolveMarketDetailParticipantsCount(collection) {
  const api = sumMarketTraders(collection);
  const added = Number.isFinite(api) ? Math.max(0, api) : 0;
  return MARKET_DETAIL_PARTICIPANTS_BASE + added;
}
